/**
 * The fetching half of the approval card.
 *
 * `AgentActionCard` is deliberately presentational — it takes an action and two
 * handlers and owns no data. This is the piece that was left for the increment
 * that ratified the endpoints, and it is separate for the same reason skySchedule
 * keeps them apart: the card renders in two places (inline in the chat, and in the
 * drawer's pending list) and only one of them wants to fetch by id.
 *
 * ## It renders from the SERVER's copy, never from the transcript
 *
 * The chat message carries only `[SKYAGENT_ACTION:<id>]`. Everything shown comes
 * from a fresh read of that id, which is what makes a re-opened thread honest: a
 * proposal from yesterday shows as executed, rejected or expired rather than
 * re-rendering a pending card whose Approve button would fail on click. It also
 * means the preview a human approves is the one the server built, not one the
 * client reconstructed from a stale message.
 */
import { SkyBody1, SkyCard, SkyLoader } from "@skypointcloud/skypoint-ui";
import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { isAgentActionType } from "../../../../lib/enums/agentActionType";
import {
  useApproveAgentActionMutation,
  useGetAgentActionQuery,
  useRejectAgentActionMutation,
} from "../../../store/api/crmAgentActionsApi";
import { AgentActionCard, humanizeActionError } from "./AgentActionCard";

export interface AgentActionCardContainerProps {
  actionId: string;
  /** False renders the read-only pending state. Resolved by the caller from the
   *  same capability the propose route gates on. */
  canApprove?: boolean;
}

/** A card-shaped shell, so a loading or failed action does not reflow the thread. */
function CardShell({ children }: { children: React.ReactNode }) {
  return (
    <SkyCard className="mt-2 overflow-hidden border border-border">
      <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">{children}</div>
    </SkyCard>
  );
}

export function AgentActionCardContainer({
  actionId,
  canApprove = false,
}: AgentActionCardContainerProps) {
  const { data, isLoading, isError } = useGetAgentActionQuery(actionId);
  const [approve, approveState] = useApproveAgentActionMutation();
  const [reject, rejectState] = useRejectAgentActionMutation();

  /**
   * A refusal from the approve call — expired, already decided, or re-validation
   * blockers. Held locally because it is about THIS click rather than about the
   * row: the row itself is refetched by the invalidation, and showing a stale
   * "could not apply" next to a card that has since succeeded would be worse than
   * showing nothing.
   */
  const [refusal, setRefusal] = useState<string[] | null>(null);

  if (isLoading) {
    return (
      <CardShell>
        <SkyLoader size="tiny" /> Loading proposal…
      </CardShell>
    );
  }

  // A 404 here is the SEC-1b anti-leak answer as much as a genuine miss — the
  // route cannot distinguish "no such action" from "an action in a community you
  // cannot see", and neither may this.
  if (isError || !data || !data.enabled) {
    return (
      <CardShell>
        <AlertTriangle
          className="h-3.5 w-3.5 shrink-0"
          aria-hidden="true"
        />
        This proposal is no longer available.
      </CardShell>
    );
  }

  const row = data.action;
  if (!isAgentActionType(row.actionType)) {
    // Proposed by a build that had an action this one does not. Fail closed and
    // say so rather than rendering an unlabelled Approve button for a write
    // nobody can identify — which is exactly what the approve route does too.
    return (
      <CardShell>
        <AlertTriangle
          className="h-3.5 w-3.5 shrink-0"
          aria-hidden="true"
        />
        This proposal is from an older version and can no longer be applied.
      </CardShell>
    );
  }

  const busy = approveState.isLoading || rejectState.isLoading;

  const onApprove = async (id: string, edits?: Record<string, string>) => {
    setRefusal(null);
    try {
      await approve({ actionId: id, edits }).unwrap();
    } catch (error) {
      const body = (error as { data?: { blockers?: string[]; error?: string } })?.data;
      setRefusal(body?.blockers?.length ? body.blockers : [humanizeActionError(body?.error)]);
    }
  };

  const onReject = async (id: string) => {
    setRefusal(null);
    try {
      await reject({ actionId: id }).unwrap();
    } catch (error) {
      const body = (error as { data?: { error?: string } })?.data;
      setRefusal([humanizeActionError(body?.error)]);
    }
  };

  return (
    <div>
      <AgentActionCard
        action={{
          id: row.id,
          actionType: row.actionType,
          // `expired` has no card state of its own; it reads as a rejection that
          // nobody made, which is the honest rendering — the proposal is closed
          // and no write happened.
          status: row.status === "expired" ? "rejected" : row.status,
          summary: row.preview?.title ?? "",
          preview: row.preview,
          error: row.error,
        }}
        canApprove={canApprove && row.executionMode !== "draft_only"}
        busy={busy}
        onApprove={onApprove}
        onReject={onReject}
      />

      {/*
        Why it could not be applied. These are the server's blockers verbatim —
        "she was moved to Closed Won two minutes ago" is the whole value of the
        re-validation, and paraphrasing it on the client would lose it.
      */}
      {refusal && (
        <div className="mt-1.5 space-y-1 px-3 text-sm text-destructive">
          {refusal.map((line) => (
            <div
              key={line}
              className="flex items-start gap-1.5"
            >
              <AlertTriangle
                className="mt-0.5 h-3 w-3 shrink-0"
                aria-hidden="true"
              />
              <SkyBody1 className="block text-sm">{line}</SkyBody1>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
